import React, { useEffect, useMemo, useState, useRef } from 'react';
import { SearchIcon, ChevronDownIcon, SettingsIcon, ActivityIcon, RefreshCwIcon, PlusIcon, Trash2Icon, CheckCircleIcon, XCircleIcon, XIcon, AlertTriangleIcon, Loader2Icon, ServerIcon, ShieldIcon, StethoscopeIcon, PowerIcon, FileTextIcon, SaveIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MachineStatusIndicator } from '../components/ui/MachineStatusIndicator';
import { ModalDialog } from '../components/ui/ModalDialog';
import { useIsMobile } from '../hooks/useMediaQuery';
// --- Types ---
type MachineStatus = 'Connesso' | 'Disconnesso' | 'Manutenzione' | 'Errore' | 'Non in funzione' | 'Test';
interface Machine {
  id: string;
  name: string;
  code: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  type: string;
  usage: string;
  exams: string[];
  doctors: string[];
  department: string;
  status: MachineStatus;
  previousStatus?: MachineStatus; // To restore after maintenance
  ip: string;
  port: string;
  protocol: string;
  endpoint: string;
  timeout: string;
  authMethod: string;
  lastSync: string;
  lastActivity: string;
  lastTest: string;
  lastError: string;
  syncStatus: string;
  notes: string;
  maintenanceDate?: string;
}
// --- Constants & Mock Data ---
const MACHINE_TYPES = ['Risonanza magnetica', 'TAC', 'Ecografo', 'Radiografo', 'Mammografo', 'Risonanza magnetica aperta'];
const EXAM_TYPES = ['RM Encefalo', 'RM Colonna', 'TAC Torace', 'TAC Addome', 'Ecografia Addome', 'Ecografia Pelvi', 'Radiografia Torace', 'Radiografia Colonna', 'Mammografia'];
const DEPARTMENTS = ['Radiologia', 'Diagnostica per Immagini', 'Senologia', 'Pronto Soccorso'];
const USAGE_TYPES = ['Diagnostica', 'Screening', 'Urgenza'];
const PROTOCOLS = ['DICOM 3.0', 'HL7 FHIR', 'REST API', 'Proprietario'];
const AUTH_METHODS = ['Certificato TLS', 'Username/Password', 'Token API', 'Nessuno'];
const DOCTORS = ['Dr. Salvatore Verdi', 'Dr.ssa Laura Bianchi', 'Dr. Marco Gialli', 'Dr.ssa Anna Neri', 'Dr. Giuseppe Romano', 'Dr.ssa Elena Colombo'];
const initialMachines: Machine[] = [{
  id: 'MAC-001',
  name: 'Siemens Magnetom Aera',
  code: 'RM-01',
  manufacturer: 'Siemens Healthineers',
  model: 'Magnetom Aera 1.5T',
  serialNumber: 'SN-10293847',
  type: 'Risonanza magnetica',
  usage: 'Diagnostica',
  exams: ['RM Encefalo', 'RM Colonna'],
  doctors: ['Dr. Salvatore Verdi', 'Dr. Marco Gialli'],
  department: 'Radiologia',
  status: 'Connesso',
  ip: '192.168.1.101',
  port: '104',
  protocol: 'DICOM 3.0',
  endpoint: 'SIEMENS_AERA_AE',
  timeout: '30s',
  authMethod: 'Certificato TLS',
  lastSync: '2026-03-11 08:15:22',
  lastActivity: '2026-03-11 09:30:00',
  lastTest: '2026-03-10 23:00:00',
  lastError: 'Nessuno',
  syncStatus: 'Sincronizzato',
  notes: 'Manutenzione ordinaria programmata per il mese prossimo.'
}, {
  id: 'MAC-002',
  name: 'GE Revolution CT',
  code: 'TC-01',
  manufacturer: 'GE Healthcare',
  model: 'Revolution CT 256',
  serialNumber: 'SN-98765432',
  type: 'TAC',
  usage: 'Urgenza',
  exams: ['TAC Torace', 'TAC Addome'],
  doctors: ['Dr.ssa Anna Neri', 'Dr.ssa Elena Colombo'],
  department: 'Pronto Soccorso',
  status: 'Connesso',
  ip: '192.168.1.102',
  port: '104',
  protocol: 'DICOM 3.0',
  endpoint: 'GE_REV_AE',
  timeout: '15s',
  authMethod: 'Certificato TLS',
  lastSync: '2026-03-11 09:45:10',
  lastActivity: '2026-03-11 09:50:00',
  lastTest: '2026-03-11 06:00:00',
  lastError: 'Nessuno',
  syncStatus: 'Sincronizzato',
  notes: 'Macchinario ad alta priorità per il PS.'
}, {
  id: 'MAC-003',
  name: 'Philips Affiniti 70',
  code: 'ECO-01',
  manufacturer: 'Philips',
  model: 'Affiniti 70',
  serialNumber: 'SN-56473829',
  type: 'Ecografo',
  usage: 'Diagnostica',
  exams: ['Ecografia Addome', 'Ecografia Pelvi'],
  doctors: ['Dr. Marco Gialli', 'Dr.ssa Anna Neri'],
  department: 'Diagnostica per Immagini',
  status: 'Connesso',
  ip: '192.168.1.103',
  port: '104',
  protocol: 'DICOM 3.0',
  endpoint: 'PHILIPS_AFF_AE',
  timeout: '30s',
  authMethod: 'Username/Password',
  lastSync: '2026-03-11 07:30:00',
  lastActivity: '2026-03-11 08:45:00',
  lastTest: '2026-03-09 18:00:00',
  lastError: 'Nessuno',
  syncStatus: 'Sincronizzato',
  notes: 'Sonda lineare sostituita di recente.'
}, {
  id: 'MAC-004',
  name: 'Carestream DRX-Evolution',
  code: 'RX-01',
  manufacturer: 'Carestream',
  model: 'DRX-Evolution Plus',
  serialNumber: 'SN-11223344',
  type: 'Radiografo',
  usage: 'Diagnostica',
  exams: ['Radiografia Torace', 'Radiografia Colonna'],
  doctors: ['Dr. Giuseppe Romano', 'Dr. Marco Gialli'],
  department: 'Radiologia',
  status: 'Manutenzione',
  previousStatus: 'Connesso',
  ip: '192.168.1.104',
  port: '104',
  protocol: 'DICOM 3.0',
  endpoint: 'CARESTREAM_AE',
  timeout: '30s',
  authMethod: 'Nessuno',
  lastSync: '2026-03-10 16:00:00',
  lastActivity: '2026-03-10 15:30:00',
  lastTest: '2026-03-10 16:05:00',
  lastError: 'Errore calibrazione detettore',
  syncStatus: 'In attesa',
  notes: 'In attesa di intervento tecnico per calibrazione.',
  maintenanceDate: '2026-03-11'
}, {
  id: 'MAC-005',
  name: 'Hologic Selenia Dimensions',
  code: 'MAM-01',
  manufacturer: 'Hologic',
  model: 'Selenia Dimensions 3D',
  serialNumber: 'SN-99887766',
  type: 'Mammografo',
  usage: 'Screening',
  exams: ['Mammografia'],
  doctors: ['Dr.ssa Laura Bianchi'],
  department: 'Senologia',
  status: 'Connesso',
  ip: '192.168.1.105',
  port: '104',
  protocol: 'DICOM 3.0',
  endpoint: 'HOLOGIC_AE',
  timeout: '45s',
  authMethod: 'Certificato TLS',
  lastSync: '2026-03-11 09:00:00',
  lastActivity: '2026-03-11 09:15:00',
  lastTest: '2026-03-08 20:00:00',
  lastError: 'Nessuno',
  syncStatus: 'Sincronizzato',
  notes: 'Utilizzato principalmente per screening preventivo.'
}, {
  id: 'MAC-006',
  name: 'Hitachi Oasis 1.2T',
  code: 'RMA-01',
  manufacturer: 'Hitachi',
  model: 'Oasis 1.2T',
  serialNumber: 'SN-55443322',
  type: 'Risonanza magnetica aperta',
  usage: 'Diagnostica',
  exams: ['RM Encefalo', 'RM Colonna'],
  doctors: ['Dr. Salvatore Verdi'],
  department: 'Radiologia',
  status: 'Non in funzione',
  ip: '192.168.1.106',
  port: '104',
  protocol: 'DICOM 3.0',
  endpoint: 'HITACHI_OASIS_AE',
  timeout: '60s',
  authMethod: 'Username/Password',
  lastSync: '2026-03-09 18:00:00',
  lastActivity: '2026-03-09 17:30:00',
  lastTest: '2026-03-10 08:00:00',
  lastError: 'Timeout connessione di rete',
  syncStatus: 'Errore sincronizzazione',
  notes: 'Problemi di rete intermittenti segnalati al reparto IT.'
}];
export function Machines() {
  const isMobile = useIsMobile();
  // --- State ---
  const [machines, setMachines] = useState<Machine[]>(initialMachines);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('Tutti');
  const [examFilter, setExamFilter] = useState('Tutti');
  const [statusFilter, setStatusFilter] = useState('Tutti');
  // Modals
  type ModalType = 'detail' | 'configure' | 'add' | 'delete' | null;
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  // Form State
  const [machineForm, setMachineForm] = useState<Partial<Machine>>({});
  // Test Connection State
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  // Popover State for Exams Overflow
  const [expandedExamsMachineId, setExpandedExamsMachineId] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Toast
  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  // --- Derived Data ---
  const filteredMachines = useMemo(() => {
    return machines.filter(mac => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = mac.name.toLowerCase().includes(q) || mac.code.toLowerCase().includes(q) || mac.manufacturer.toLowerCase().includes(q) || mac.model.toLowerCase().includes(q);
      const matchesType = typeFilter === 'Tutti' || mac.type === typeFilter;
      const matchesExam = examFilter === 'Tutti' || mac.exams.includes(examFilter);
      const matchesStatus = statusFilter === 'Tutti' || mac.status === statusFilter;
      return matchesSearch && matchesType && matchesExam && matchesStatus;
    });
  }, [machines, searchQuery, typeFilter, examFilter, statusFilter]);
  // --- Effects ---
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setExpandedExamsMachineId(null);
      }
    };
    if (expandedExamsMachineId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedExamsMachineId]);
  // --- Handlers ---
  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({
      type,
      text
    });
    setTimeout(() => setToastMessage(null), 4000);
  };
  const handleOpenDetail = (mac: Machine) => {
    setSelectedMachine(mac);
    setActiveModal('detail');
  };
  const handleOpenConfigure = (mac: Machine, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedMachine(mac);
    setMachineForm({
      ...mac
    });
    setActiveModal('configure');
  };
  const handleOpenAdd = () => {
    setSelectedMachine(null);
    setMachineForm({
      name: '',
      code: '',
      manufacturer: '',
      model: '',
      serialNumber: '',
      type: MACHINE_TYPES[0],
      usage: USAGE_TYPES[0],
      exams: [],
      doctors: [],
      department: DEPARTMENTS[0],
      status: 'Disconnesso',
      ip: '',
      port: '104',
      protocol: PROTOCOLS[0],
      endpoint: '',
      timeout: '30s',
      authMethod: AUTH_METHODS[0],
      notes: ''
    });
    setActiveModal('add');
  };
  const handleSaveMachine = () => {
    if (!machineForm.name || !machineForm.code || !machineForm.ip) {
      showToast('error', 'Compila i campi obbligatori (Nome, Codice, IP).');
      return;
    }
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    if (activeModal === 'add') {
      const newMac: Machine = {
        ...(machineForm as Machine),
        id: `MAC-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
        lastSync: 'Mai',
        lastActivity: 'Mai',
        lastTest: 'Mai',
        lastError: 'Nessuno',
        syncStatus: 'In attesa'
      };
      setMachines(prev => [...prev, newMac]);
      showToast('success', 'Macchinario aggiunto con successo.');
    } else if (activeModal === 'configure' && selectedMachine) {
      const updatedMac = {
        ...selectedMachine,
        ...machineForm
      } as Machine;
      setMachines(prev => prev.map(m => m.id === selectedMachine.id ? updatedMac : m));
      setSelectedMachine(updatedMac); // Update detail view if we go back to it
      showToast('success', 'Configurazione macchinario aggiornata.');
    }
    setActiveModal(activeModal === 'configure' ? 'detail' : null);
  };
  const confirmDelete = () => {
    setActiveModal('delete');
  };
  const executeDelete = () => {
    if (!selectedMachine) return;
    setMachines(prev => prev.filter(m => m.id !== selectedMachine.id));
    setActiveModal(null);
    setSelectedMachine(null);
    showToast('success', 'Macchinario eliminato con successo.');
  };
  const handleToggleMaintenance = () => {
    if (!selectedMachine) return;
    const isCurrentlyMaintenance = selectedMachine.status === 'Manutenzione';
    const newStatus = isCurrentlyMaintenance ? selectedMachine.previousStatus || 'Connesso' : 'Manutenzione';
    const updatedMac: Machine = {
      ...selectedMachine,
      status: newStatus,
      previousStatus: isCurrentlyMaintenance ? undefined : selectedMachine.status,
      maintenanceDate: isCurrentlyMaintenance ? undefined : new Date().toISOString().split('T')[0]
    };
    setMachines(prev => prev.map(m => m.id === selectedMachine.id ? updatedMac : m));
    setSelectedMachine(updatedMac);
    showToast('success', isCurrentlyMaintenance ? 'Macchinario ripristinato come operativo.' : 'Macchinario impostato in manutenzione.');
  };
  const handleTestConnection = () => {
    setIsTesting(true);
    setTestResult(null);
    setTimeout(() => {
      setIsTesting(false);
      // Check if machine is in a state that cannot be tested
      if (selectedMachine && ['Non in funzione', 'Disconnesso', 'Manutenzione'].includes(selectedMachine.status)) {
        setTestResult({
          success: false,
          message: `Impossibile testare la connessione. Il macchinario è attualmente in stato: ${selectedMachine.status}.`
        });
        setTimeout(() => setTestResult(null), 5000);
        return;
      }
      const isSuccess = Math.random() > 0.2; // 80% success rate
      setTestResult({
        success: isSuccess,
        message: isSuccess ? 'Connessione riuscita. La piattaforma comunica correttamente con il macchinario.' : 'Connessione non riuscita. Verificare configurazione e stato del macchinario.'
      });
      // Auto dismiss result
      setTimeout(() => setTestResult(null), 5000);
    }, 2000);
  };
  // Checkbox toggle helper
  const toggleArrayItem = (array: string[] = [], item: string) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    }
    return [...array, item];
  };
  // --- Render Helpers ---
  const renderExamsWithOverflow = (machine: Machine) => {
    const maxVisible = 3;
    const visibleExams = machine.exams.slice(0, maxVisible);
    const overflowCount = machine.exams.length - maxVisible;
    const isExpanded = expandedExamsMachineId === machine.id;
    return <div className="relative flex flex-wrap gap-1.5 items-center" data-id="element-1260">
        {visibleExams.map(exam => <span key={exam} className="px-2 py-1 bg-clinical-gray-100 text-text-secondary rounded-md text-xs whitespace-nowrap" data-id="element-1261">
            {exam}
          </span>)}

        {overflowCount > 0 && <div className="relative" data-id="element-1262">
            <button onClick={e => {
          e.stopPropagation();
          setExpandedExamsMachineId(isExpanded ? null : machine.id);
        }} className="px-2 py-1 bg-medical-green-50 text-medical-green hover:bg-medical-green-100 rounded-md text-xs font-medium cursor-pointer transition-colors" data-id="element-1263">
              +{overflowCount}
            </button>

            <AnimatePresence data-id="element-1264">
              {isExpanded && <motion.div ref={popoverRef} initial={{
            opacity: 0,
            y: 5,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 5,
            scale: 0.95
          }} transition={{
            duration: 0.15
          }} className="absolute z-[60] top-full left-0 mt-2 w-64 bg-white rounded-medical-lg shadow-modal border border-clinical-gray-200 p-3" onClick={e => e.stopPropagation()} data-id="element-1265">
                  <h4 className="text-xs font-semibold text-text-primary uppercase tracking-wider mb-2 pb-2 border-b border-clinical-gray-100" data-id="element-1266">
                    Tutti gli esami compatibili
                  </h4>
                  <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto no-scrollbar" data-id="element-1267">
                    {machine.exams.map(exam => <span key={exam} className="px-2 py-1 bg-clinical-gray-100 text-text-secondary rounded-md text-xs" data-id="element-1268">
                        {exam}
                      </span>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>}
        {machine.exams.length === 0 && <span className="text-xs text-text-tertiary" data-id="element-1269">
            Nessun esame configurato
          </span>}
      </div>;
  };
  return <div className="space-y-6 w-full min-w-0 pb-12" data-id="element-1270">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1271">
        <div data-id="element-1272">
          <h1 className="text-2xl md:text-page-title mb-1 md:mb-2 text-text-primary font-bold" data-id="element-1273">
            Macchinari
          </h1>
          <p className="text-sm md:text-body text-text-secondary" data-id="element-1274">
            Gestione connessioni apparecchiature diagnostiche
          </p>
        </div>
        <button onClick={handleOpenAdd} className="medical-btn-primary w-full sm:w-auto shrink-0" data-id="element-1275">
          <PlusIcon size={18} className="mr-1.5" data-id="element-1276" /> Aggiungi macchinario
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center bg-white p-4 rounded-medical-lg shadow-sm border border-clinical-gray-100" data-id="element-1277">
        <div className="relative w-full md:w-72" data-id="element-1278">
          <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1279" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca per nome, codice, produttore, modello..." className="medical-input pl-9 text-sm h-9 w-full" data-id="element-1280" />
        </div>

        <div className="relative w-full md:w-auto" data-id="element-1281">
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-1282">
            <option value="Tutti" data-id="element-1283">Tipo macchinario: Tutti</option>
            {MACHINE_TYPES.map(type => <option key={type} value={type} data-id="element-1284">
                {type}
              </option>)}
          </select>
          <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1285" />
        </div>

        <div className="relative w-full md:w-auto" data-id="element-1286">
          <select value={examFilter} onChange={e => setExamFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-1287">
            <option value="Tutti" data-id="element-1288">Esame compatibile: Tutti</option>
            {EXAM_TYPES.map(exam => <option key={exam} value={exam} data-id="element-1289">
                {exam}
              </option>)}
          </select>
          <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1290" />
        </div>

        <div className="relative w-full md:w-auto" data-id="element-1291">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-1292">
            <option value="Tutti" data-id="element-1293">Stato: Tutti</option>
            <option value="Connesso" data-id="element-1294">Connesso</option>
            <option value="Disconnesso" data-id="element-1295">Disconnesso</option>
            <option value="Non in funzione" data-id="element-1296">Non in funzione</option>
            <option value="Manutenzione" data-id="element-1297">Manutenzione</option>
            <option value="Errore" data-id="element-1298">Errore</option>
          </select>
          <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1299" />
        </div>
      </div>

      {/* Grid */}
      {filteredMachines.length === 0 ? <div className="p-12 text-center bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" data-id="element-1300">
          <ServerIcon size={48} className="mx-auto text-clinical-gray-300 mb-4" data-id="element-1301" />
          <h3 className="text-lg font-medium text-text-primary mb-2" data-id="element-1302">
            Nessun macchinario trovato
          </h3>
          <p className="text-text-secondary" data-id="element-1303">
            Modifica i filtri di ricerca o aggiungi un nuovo macchinario.
          </p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-1304">
          {filteredMachines.map(machine => <div key={machine.id} className={`medical-card-hover p-4 md:p-6 cursor-pointer relative overflow-hidden ${machine.status === 'Manutenzione' ? 'ring-1 ring-status-warning border-status-warning/30' : machine.status === 'Non in funzione' ? 'ring-1 ring-red-500 border-red-300' : ''}`} onClick={() => handleOpenDetail(machine)} data-id="element-1305">
              <div className="flex items-start justify-between mb-4 gap-2" data-id="element-1306">
                <div className="min-w-0 flex-1" data-id="element-1307">
                  <div className="flex items-center gap-2 mb-1" data-id="element-1308">
                    <h3 className="text-lg md:text-section-title text-text-primary font-semibold truncate" data-id="element-1309">
                      {machine.name}
                    </h3>
                  </div>
                  <p className="text-sm text-text-secondary truncate" data-id="element-1310">
                    {machine.type} • {machine.code}
                  </p>
                </div>
                <div className="shrink-0" data-id="element-1311">
                  <MachineStatusIndicator status={machine.status} data-id="element-1312" />
                </div>
              </div>

              <div className="mb-6" data-id="element-1313">
                <p className="text-xs text-text-tertiary uppercase tracking-wider mb-2" data-id="element-1314">
                  Esami compatibili
                </p>
                {renderExamsWithOverflow(machine)}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-clinical-gray-100 mt-auto" data-id="element-1315">
                <span className="text-[10px] md:text-xs text-text-tertiary truncate mr-2" data-id="element-1316">
                  Ultima sync:{' '}
                  {machine.lastSync.split(' ')[1] || machine.lastSync}
                </span>
                <button onClick={e => handleOpenConfigure(machine, e)} className="text-medical-green hover:text-medical-green-light text-sm font-medium flex items-center gap-1.5 shrink-0 transition-colors bg-medical-green-50 hover:bg-medical-green-100 px-2.5 py-1.5 rounded-md" data-id="element-1317">
                  <SettingsIcon size={14} data-id="element-1318" /> Configura
                </button>
              </div>
            </div>)}
        </div>}

      {/* --- Modals --- */}

      {/* Detail Modal */}
      <ModalDialog isOpen={activeModal === 'detail'} onClose={() => {
      setActiveModal(null);
      setTestResult(null);
    }} title="Dettaglio Macchinario" maxWidth="max-w-5xl" footer={<div className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-3" data-id="element-1320">
            <button onClick={confirmDelete} className="text-status-error hover:text-red-700 text-sm font-medium flex items-center gap-1.5 px-3 py-2 rounded hover:bg-status-error-bg transition-colors whitespace-nowrap shrink-0" data-id="element-1321">
              <Trash2Icon size={16} data-id="element-1322" /> Elimina
            </button>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center" data-id="element-1323">
              <button onClick={handleToggleMaintenance} className={`medical-btn-secondary py-2 px-3 text-sm whitespace-nowrap justify-center ${selectedMachine?.status === 'Manutenzione' ? 'text-status-success border-status-success/30 hover:bg-status-success-bg' : 'text-status-warning border-status-warning/30 hover:bg-status-warning-bg'}`} data-id="element-1324">
                <PowerIcon size={16} className="mr-1" data-id="element-1325" />
                {selectedMachine?.status === 'Manutenzione' ? 'Ripristina operatività' : 'Manutenzione'}
              </button>
              <button onClick={() => handleOpenConfigure(selectedMachine!)} className="medical-btn-secondary py-2 px-3 text-sm whitespace-nowrap justify-center" data-id="element-1326">
                <SettingsIcon size={16} className="mr-1" data-id="element-1327" /> Configura
              </button>
              <button onClick={handleTestConnection} disabled={isTesting} className="medical-btn-primary py-2 px-3 text-sm whitespace-nowrap justify-center disabled:opacity-70" data-id="element-1328">
                {isTesting ? <>
                    <Loader2Icon size={16} className="mr-1 animate-spin" data-id="element-1329" />
                    Test in corso...
                  </> : <>
                    <RefreshCwIcon size={16} className="mr-1" data-id="element-1330" /> Test
                    connessione
                  </>}
              </button>
            </div>
          </div>} data-id="element-1319">
        {selectedMachine && <div className="space-y-6" data-id="element-1331">
            {/* Test Result Alert */}
            <AnimatePresence data-id="element-1332">
              {testResult && <motion.div initial={{
            opacity: 0,
            height: 0,
            marginBottom: 0
          }} animate={{
            opacity: 1,
            height: 'auto',
            marginBottom: 16
          }} exit={{
            opacity: 0,
            height: 0,
            marginBottom: 0
          }} className="overflow-hidden" data-id="element-1333">
                  <div className={`p-4 rounded-medical flex items-start gap-3 border ${testResult.success ? 'bg-status-success-bg border-status-success/20' : 'bg-status-error-bg border-status-error/20'}`} data-id="element-1334">
                    {testResult.success ? <CheckCircleIcon className="text-status-success shrink-0 mt-0.5" size={20} data-id="element-1335" /> : <XCircleIcon className="text-status-error shrink-0 mt-0.5" size={20} data-id="element-1336" />}
                    <div className="flex-1" data-id="element-1337">
                      <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`} data-id="element-1338">
                        {testResult.message}
                      </p>
                    </div>
                    <button onClick={() => setTestResult(null)} className="text-text-tertiary hover:text-text-primary" data-id="element-1339">
                      <XIcon size={16} data-id="element-1340" />
                    </button>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* Header Area */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-6 border-b border-clinical-gray-100" data-id="element-1341">
              <div data-id="element-1342">
                <div className="flex items-center gap-3 mb-1" data-id="element-1343">
                  <h2 className="text-xl font-bold text-text-primary" data-id="element-1344">
                    {selectedMachine.name}
                  </h2>
                  <span className="px-2 py-0.5 bg-clinical-gray-100 text-text-secondary rounded text-xs font-medium" data-id="element-1345">
                    {selectedMachine.code}
                  </span>
                </div>
                <p className="text-text-secondary" data-id="element-1346">
                  {selectedMachine.manufacturer} {selectedMachine.model}
                </p>
                <p className="text-xs text-text-tertiary mt-1" data-id="element-1347">
                  SN: {selectedMachine.serialNumber}
                </p>
              </div>
              <div className="flex flex-col items-start sm:items-end gap-2 bg-clinical-gray-50 p-3 rounded-medical border border-clinical-gray-200" data-id="element-1348">
                <MachineStatusIndicator status={selectedMachine.status} data-id="element-1349" />
                {selectedMachine.status === 'Connesso' && <span className="px-2 py-1 bg-status-success-bg text-status-success text-xs font-medium rounded border border-status-success/20" data-id="element-1350">
                    Operativo
                  </span>}
                {selectedMachine.status === 'Manutenzione' && <span className="px-2 py-1 bg-status-warning-bg text-status-warning text-xs font-medium rounded border border-status-warning/20" data-id="element-1351">
                    In manutenzione programmata dal{' '}
                    {selectedMachine.maintenanceDate || 'Oggi'}
                  </span>}
                {selectedMachine.status === 'Non in funzione' && <span className="px-2 py-1 bg-status-error-bg text-status-error text-xs font-medium rounded border border-status-error/20" data-id="element-1352">
                    Non disponibile
                  </span>}
                {selectedMachine.status === 'Errore' && <span className="px-2 py-1 bg-status-error-bg text-status-error text-xs font-medium rounded border border-status-error/20" data-id="element-1353">
                    Errore di comunicazione
                  </span>}
                {selectedMachine.status === 'Disconnesso' && <span className="px-2 py-1 bg-clinical-gray-200 text-text-secondary text-xs font-medium rounded border border-clinical-gray-300" data-id="element-1354">
                    In attesa di connessione
                  </span>}
                {selectedMachine.status === 'Test' && <span className="px-2 py-1 bg-status-info-bg text-status-info text-xs font-medium rounded border border-status-info/20" data-id="element-1355">
                    Test in corso
                  </span>}
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1356">
              <div className="space-y-4" data-id="element-1357">
                <div data-id="element-1358">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1359">
                    Tipologia
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1360">
                    {selectedMachine.type}
                  </span>
                </div>
                <div data-id="element-1361">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1362">
                    Tipo di utilizzo
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1363">
                    {selectedMachine.usage}
                  </span>
                </div>
                <div data-id="element-1364">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1365">
                    Reparto
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1366">
                    {selectedMachine.department}
                  </span>
                </div>
              </div>
              <div className="space-y-4" data-id="element-1367">
                <div data-id="element-1368">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1369">
                    Ultima sincronizzazione
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1370">
                    {selectedMachine.lastSync}
                  </span>
                </div>
                <div data-id="element-1371">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1372">
                    Ultima attività
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1373">
                    {selectedMachine.lastActivity}
                  </span>
                </div>
                <div data-id="element-1374">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1375">
                    Stato sincronizzazione
                  </span>
                  <span className={`text-sm font-medium ${selectedMachine.syncStatus === 'Sincronizzato' ? 'text-status-success' : selectedMachine.syncStatus === 'In attesa' ? 'text-status-warning' : 'text-status-error'}`} data-id="element-1376">
                    {selectedMachine.syncStatus}
                  </span>
                </div>
              </div>
            </div>

            {/* Bidirectional Communication Status */}
            <div className="bg-clinical-gray-50 border border-clinical-gray-200 rounded-medical-lg p-5" data-id="element-1377">
              <h4 className="text-sm font-semibold text-text-primary mb-4 flex items-center gap-2" data-id="element-1378">
                <RefreshCwIcon size={16} className="text-text-tertiary" data-id="element-1379" />{' '}
                Integrazione piattaforma-macchinario
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5" data-id="element-1380">
                <div data-id="element-1381">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1382">
                    Modalità comunicazione
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1383">
                    Automatica
                  </span>
                </div>
                <div data-id="element-1384">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1385">
                    Stato connessione
                  </span>
                  <MachineStatusIndicator status={selectedMachine.status} data-id="element-1386" />
                </div>
                <div data-id="element-1387">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1388">
                    Stato sincronizzazione
                  </span>
                  <span className={`text-sm font-medium ${selectedMachine.syncStatus === 'Sincronizzato' ? 'text-status-success' : selectedMachine.syncStatus === 'In attesa' ? 'text-status-warning' : 'text-status-error'}`} data-id="element-1389">
                    {selectedMachine.syncStatus}
                  </span>
                </div>
                <div data-id="element-1390">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1391">
                    Ultima comunicazione inviata
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1392">
                    {selectedMachine.lastSync}
                  </span>
                  <p className="text-[11px] text-text-tertiary mt-0.5" data-id="element-1393">
                    Dati paziente e richiesta esame → macchinario
                  </p>
                </div>
                <div data-id="element-1394">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1395">
                    Ultima comunicazione ricevuta
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1396">
                    {selectedMachine.lastActivity}
                  </span>
                  <p className="text-[11px] text-text-tertiary mt-0.5" data-id="element-1397">
                    Risultati esame e dati diagnostici ← macchinario
                  </p>
                </div>
                <div data-id="element-1398">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1399">
                    Protocollo attivo
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1400">
                    {selectedMachine.protocol}
                  </span>
                  <p className="text-[11px] text-text-tertiary mt-0.5" data-id="element-1401">
                    Sincronizzazione automatica dopo configurazione
                  </p>
                </div>
              </div>
            </div>

            {/* Exams */}
            <div data-id="element-1402">
              <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-2" data-id="element-1403">
                Esami compatibili
              </span>
              <div className="flex flex-wrap gap-1.5" data-id="element-1404">
                {selectedMachine.exams.map(exam => <span key={exam} className="px-2.5 py-1.5 bg-clinical-gray-100 text-text-secondary rounded-md text-sm" data-id="element-1405">
                    {exam}
                  </span>)}
                {selectedMachine.exams.length === 0 && <span className="text-sm text-text-tertiary" data-id="element-1406">
                    Nessun esame configurato
                  </span>}
              </div>
            </div>

            {/* Technical Config Summary */}
            <div data-id="element-1407">
              <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2" data-id="element-1408">
                <ServerIcon size={16} className="text-text-tertiary" data-id="element-1409" />{' '}
                Configurazione tecnica sintetica
              </h4>
              <div className="bg-white border border-clinical-gray-200 rounded-medical overflow-hidden" data-id="element-1410">
                <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-clinical-gray-100" data-id="element-1411">
                  <div className="p-4 space-y-3" data-id="element-1412">
                    <div className="flex justify-between" data-id="element-1413">
                      <span className="text-xs text-text-secondary" data-id="element-1414">
                        Indirizzo IP
                      </span>
                      <span className="text-sm font-medium text-text-primary font-mono" data-id="element-1415">
                        {selectedMachine.ip}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1416">
                      <span className="text-xs text-text-secondary" data-id="element-1417">Porta</span>
                      <span className="text-sm font-medium text-text-primary font-mono" data-id="element-1418">
                        {selectedMachine.port}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1419">
                      <span className="text-xs text-text-secondary" data-id="element-1420">
                        Protocollo
                      </span>
                      <span className="text-sm font-medium text-text-primary" data-id="element-1421">
                        {selectedMachine.protocol}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 space-y-3" data-id="element-1422">
                    <div className="flex justify-between" data-id="element-1423">
                      <span className="text-xs text-text-secondary" data-id="element-1424">
                        Endpoint (AE Title)
                      </span>
                      <span className="text-sm font-medium text-text-primary font-mono" data-id="element-1425">
                        {selectedMachine.endpoint}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1426">
                      <span className="text-xs text-text-secondary" data-id="element-1427">
                        Timeout
                      </span>
                      <span className="text-sm font-medium text-text-primary" data-id="element-1428">
                        {selectedMachine.timeout}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1429">
                      <span className="text-xs text-text-secondary" data-id="element-1430">
                        Autenticazione
                      </span>
                      <span className="text-sm font-medium text-text-primary" data-id="element-1431">
                        {selectedMachine.authMethod}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Communication Log */}
            <div data-id="element-1432">
              <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2" data-id="element-1433">
                <ActivityIcon size={16} className="text-text-tertiary" data-id="element-1434" /> Log di
                comunicazione
              </h4>
              <div className="bg-clinical-gray-50 border border-clinical-gray-200 rounded-medical p-4 font-mono text-xs space-y-2 h-48 overflow-y-auto" data-id="element-1435">
                <div className="text-text-secondary" data-id="element-1436">
                  [{selectedMachine.lastSync.split(' ')[1] || '14:32:01'}]
                  C-ECHO Request sent to {selectedMachine.ip}:
                  {selectedMachine.port}
                </div>
                <div className="text-status-success" data-id="element-1437">
                  [{selectedMachine.lastSync.split(' ')[1] || '14:32:01'}]
                  C-ECHO Response received (Success)
                </div>
                <div className="text-text-secondary" data-id="element-1438">
                  [{selectedMachine.lastActivity.split(' ')[1] || '14:30:45'}]
                  Association established with {selectedMachine.endpoint}
                </div>
                <div className="text-text-secondary" data-id="element-1439">
                  [{selectedMachine.lastActivity.split(' ')[1] || '14:30:45'}]
                  Modality Worklist query sent
                </div>
                <div className="text-status-success" data-id="element-1440">
                  [{selectedMachine.lastActivity.split(' ')[1] || '14:30:46'}]
                  Modality Worklist response received (3 items)
                </div>
                {selectedMachine.lastError !== 'Nessuno' && <>
                    <div className="text-status-error mt-2" data-id="element-1441">
                      [{selectedMachine.lastTest.split(' ')[1] || '10:05:00'}]
                      ERROR: {selectedMachine.lastError}
                    </div>
                    <div className="text-status-warning" data-id="element-1442">
                      [{selectedMachine.lastTest.split(' ')[1] || '10:05:00'}]
                      Connection reset by peer
                    </div>
                  </>}
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Configure / Add Modal */}
      <ModalDialog isOpen={activeModal === 'configure' || activeModal === 'add'} onClose={() => setActiveModal(null)} title={activeModal === 'add' ? 'Aggiungi Macchinario' : 'Configura Macchinario'} maxWidth="max-w-4xl" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setActiveModal(null)} data-id="element-1444">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto" onClick={handleSaveMachine} data-id="element-1445">
              <SaveIcon size={16} className="mr-1.5" data-id="element-1446" />{' '}
              {activeModal === 'add' ? 'Salva macchinario' : 'Salva configurazione'}
            </button>
          </>} data-id="element-1443">
        <div className="space-y-8" data-id="element-1447">
          {/* Section 1: Informazioni generali */}
          <section data-id="element-1448">
            <h3 className="text-section-title text-text-primary mb-4 flex items-center gap-2" data-id="element-1449">
              <FileTextIcon size={20} className="text-medical-green" data-id="element-1450" /> 1.
              Informazioni generali
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1451">
              <div data-id="element-1452">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1453">
                  Nome macchinario *
                </label>
                <input type="text" className="medical-input" placeholder="es. Siemens Magnetom Aera" value={machineForm.name || ''} onChange={e => setMachineForm(prev => ({
                ...prev,
                name: e.target.value
              }))} data-id="element-1454" />
              </div>
              <div data-id="element-1455">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1456">
                  Codice interno *
                </label>
                <input type="text" className="medical-input" placeholder="es. RM-01" value={machineForm.code || ''} onChange={e => setMachineForm(prev => ({
                ...prev,
                code: e.target.value
              }))} data-id="element-1457" />
              </div>
              <div data-id="element-1458">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1459">
                  Tipologia macchinario
                </label>
                <div className="relative" data-id="element-1460">
                  <select className="medical-input appearance-none pr-8" value={machineForm.type || ''} onChange={e => setMachineForm(prev => ({
                  ...prev,
                  type: e.target.value
                }))} data-id="element-1461">
                    {MACHINE_TYPES.map(type => <option key={type} value={type} data-id="element-1462">
                        {type}
                      </option>)}
                  </select>
                  <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1463" />
                </div>
              </div>
              <div data-id="element-1464">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1465">
                  Produttore
                </label>
                <input type="text" className="medical-input" placeholder="es. Siemens" value={machineForm.manufacturer || ''} onChange={e => setMachineForm(prev => ({
                ...prev,
                manufacturer: e.target.value
              }))} data-id="element-1466" />
              </div>
              <div data-id="element-1467">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1468">
                  Modello
                </label>
                <input type="text" className="medical-input" placeholder="es. Magnetom Aera 1.5T" value={machineForm.model || ''} onChange={e => setMachineForm(prev => ({
                ...prev,
                model: e.target.value
              }))} data-id="element-1469" />
              </div>
              <div data-id="element-1470">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1471">
                  Numero seriale
                </label>
                <input type="text" className="medical-input" placeholder="es. SN-123456" value={machineForm.serialNumber || ''} onChange={e => setMachineForm(prev => ({
                ...prev,
                serialNumber: e.target.value
              }))} data-id="element-1472" />
              </div>
              <div data-id="element-1473">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1474">
                  Tipo di utilizzo
                </label>
                <div className="relative" data-id="element-1475">
                  <select className="medical-input appearance-none pr-8" value={machineForm.usage || ''} onChange={e => setMachineForm(prev => ({
                  ...prev,
                  usage: e.target.value
                }))} data-id="element-1476">
                    {USAGE_TYPES.map(usage => <option key={usage} value={usage} data-id="element-1477">
                        {usage}
                      </option>)}
                  </select>
                  <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1478" />
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Configurazione connessione */}
          <section data-id="element-1479">
            <h3 className="text-section-title text-text-primary mb-4 flex items-center gap-2 pt-6 border-t border-clinical-gray-200" data-id="element-1480">
              <ServerIcon size={20} className="text-medical-green" data-id="element-1481" /> 2.
              Configurazione connessione
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1482">
              <div data-id="element-1483">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1484">
                  Indirizzo IP / Host *
                </label>
                <input type="text" className="medical-input font-mono text-sm" placeholder="192.168.1.100" value={machineForm.ip || ''} onChange={e => setMachineForm(prev => ({
                ...prev,
                ip: e.target.value
              }))} data-id="element-1485" />
              </div>
              <div data-id="element-1486">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1487">
                  Porta
                </label>
                <input type="text" className="medical-input font-mono text-sm" placeholder="104" value={machineForm.port || ''} onChange={e => setMachineForm(prev => ({
                ...prev,
                port: e.target.value
              }))} data-id="element-1488" />
              </div>
              <div data-id="element-1489">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1490">
                  Protocollo di comunicazione
                </label>
                <div className="relative" data-id="element-1491">
                  <select className="medical-input appearance-none pr-8" value={machineForm.protocol || ''} onChange={e => setMachineForm(prev => ({
                  ...prev,
                  protocol: e.target.value
                }))} data-id="element-1492">
                    {PROTOCOLS.map(p => <option key={p} value={p} data-id="element-1493">
                        {p}
                      </option>)}
                  </select>
                  <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1494" />
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-1495">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1496">
                  Endpoint o percorso di integrazione (AE Title)
                </label>
                <input type="text" className="medical-input font-mono text-sm" placeholder="es. SIEMENS_AE" value={machineForm.endpoint || ''} onChange={e => setMachineForm(prev => ({
                ...prev,
                endpoint: e.target.value
              }))} data-id="element-1497" />
              </div>
              <div data-id="element-1498">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1499">
                  Timeout connessione
                </label>
                <input type="text" className="medical-input" placeholder="es. 30s" value={machineForm.timeout || ''} onChange={e => setMachineForm(prev => ({
                ...prev,
                timeout: e.target.value
              }))} data-id="element-1500" />
              </div>
            </div>
          </section>

          {/* Section 3: Credenziali e sicurezza */}
          <section data-id="element-1501">
            <h3 className="text-section-title text-text-primary mb-4 flex items-center gap-2 pt-6 border-t border-clinical-gray-200" data-id="element-1502">
              <ShieldIcon size={20} className="text-medical-green" data-id="element-1503" /> 3.
              Credenziali e sicurezza
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1504">
              <div data-id="element-1505">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1506">
                  Metodo autenticazione
                </label>
                <div className="relative" data-id="element-1507">
                  <select className="medical-input appearance-none pr-8" value={machineForm.authMethod || ''} onChange={e => setMachineForm(prev => ({
                  ...prev,
                  authMethod: e.target.value
                }))} data-id="element-1508">
                    {AUTH_METHODS.map(m => <option key={m} value={m} data-id="element-1509">
                        {m}
                      </option>)}
                  </select>
                  <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1510" />
                </div>
              </div>
              {machineForm.authMethod === 'Username/Password' && <>
                  <div data-id="element-1511">
                    <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1512">
                      Username
                    </label>
                    <input type="text" className="medical-input" placeholder="admin" data-id="element-1513" />
                  </div>
                  <div data-id="element-1514">
                    <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1515">
                      Password
                    </label>
                    <input type="password" className="medical-input" placeholder="••••••••" data-id="element-1516" />
                  </div>
                </>}
              {machineForm.authMethod === 'Token API' && <div className="sm:col-span-2" data-id="element-1517">
                  <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1518">
                    Token API
                  </label>
                  <input type="password" className="medical-input" placeholder="Inserisci token..." data-id="element-1519" />
                </div>}
            </div>
          </section>

          {/* Section 4: Compatibilità clinica */}
          <section data-id="element-1520">
            <h3 className="text-section-title text-text-primary mb-4 flex items-center gap-2 pt-6 border-t border-clinical-gray-200" data-id="element-1521">
              <StethoscopeIcon size={20} className="text-medical-green" data-id="element-1522" /> 4.
              Compatibilità clinica
            </h3>
            <div className="space-y-6" data-id="element-1523">
              <div data-id="element-1524">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1525">
                  Reparto o area di assegnazione
                </label>
                <div className="relative max-w-md" data-id="element-1526">
                  <select className="medical-input appearance-none pr-8" value={machineForm.department || ''} onChange={e => setMachineForm(prev => ({
                  ...prev,
                  department: e.target.value
                }))} data-id="element-1527">
                    {DEPARTMENTS.map(d => <option key={d} value={d} data-id="element-1528">
                        {d}
                      </option>)}
                  </select>
                  <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1529" />
                </div>
              </div>

              <div data-id="element-1530">
                <label className="block text-sm font-medium text-text-primary mb-3" data-id="element-1531">
                  Esami compatibili
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 bg-clinical-gray-50 p-4 rounded-medical border border-clinical-gray-200" data-id="element-1532">
                  {EXAM_TYPES.map(exam => <label key={exam} className="flex items-center gap-2 cursor-pointer group" data-id="element-1533">
                      <input type="checkbox" className="w-4 h-4 rounded border-clinical-gray-300 text-medical-green focus:ring-medical-green cursor-pointer" checked={(machineForm.exams || []).includes(exam)} onChange={() => setMachineForm(prev => ({
                    ...prev,
                    exams: toggleArrayItem(prev.exams, exam)
                  }))} data-id="element-1534" />
                      <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors" data-id="element-1535">
                        {exam}
                      </span>
                    </label>)}
                </div>
              </div>

              <div data-id="element-1536">
                <label className="block text-sm font-medium text-text-primary mb-3" data-id="element-1537">
                  Medici abilitati a usare il macchinario
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 bg-clinical-gray-50 p-4 rounded-medical border border-clinical-gray-200" data-id="element-1538">
                  {DOCTORS.map(doc => <label key={doc} className="flex items-center gap-2 cursor-pointer group" data-id="element-1539">
                      <input type="checkbox" className="w-4 h-4 rounded border-clinical-gray-300 text-medical-green focus:ring-medical-green cursor-pointer" checked={(machineForm.doctors || []).includes(doc)} onChange={() => setMachineForm(prev => ({
                    ...prev,
                    doctors: toggleArrayItem(prev.doctors, doc)
                  }))} data-id="element-1540" />
                      <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors" data-id="element-1541">
                        {doc}
                      </span>
                    </label>)}
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Stato operativo (only in configure/edit mode) */}
          {activeModal === 'configure' && <section data-id="element-1542">
              <h3 className="text-section-title text-text-primary mb-4 flex items-center gap-2 pt-6 border-t border-clinical-gray-200" data-id="element-1543">
                <ActivityIcon size={20} className="text-medical-green" data-id="element-1544" /> 5.
                Stato operativo
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1545">
                <div data-id="element-1546">
                  <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1547">
                    Stato connessione
                  </label>
                  <div className="relative" data-id="element-1548">
                    <select className="medical-input appearance-none pr-8" value={machineForm.status === 'Manutenzione' ? machineForm.previousStatus || 'Connesso' : machineForm.status || 'Connesso'} onChange={e => {
                  const newStatus = e.target.value as MachineStatus;
                  setMachineForm(prev => ({
                    ...prev,
                    status: prev.status === 'Manutenzione' ? 'Manutenzione' : newStatus,
                    previousStatus: prev.status === 'Manutenzione' ? newStatus : undefined
                  }));
                }} data-id="element-1549">
                      <option value="Connesso" data-id="element-1550">Connesso</option>
                      <option value="Disconnesso" data-id="element-1551">Disconnesso</option>
                      <option value="Errore" data-id="element-1552">Errore</option>
                      <option value="Non in funzione" data-id="element-1553">Non in funzione</option>
                    </select>
                    <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1554" />
                  </div>
                </div>
                <div data-id="element-1555">
                  <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1556">
                    Data ultima verifica
                  </label>
                  <input type="date" className="medical-input" defaultValue={new Date().toISOString().split('T')[0]} data-id="element-1557" />
                </div>
                <div className="sm:col-span-2 flex items-center gap-3 p-4 bg-status-warning-bg border border-status-warning/20 rounded-medical mt-2" data-id="element-1558">
                  <input type="checkbox" id="maintenance-toggle" className="w-5 h-5 rounded border-status-warning text-status-warning focus:ring-status-warning cursor-pointer" checked={machineForm.status === 'Manutenzione'} onChange={e => {
                const isMaint = e.target.checked;
                setMachineForm(prev => ({
                  ...prev,
                  status: isMaint ? 'Manutenzione' : prev.previousStatus || 'Connesso',
                  previousStatus: isMaint ? prev.status : undefined,
                  maintenanceDate: isMaint ? new Date().toISOString().split('T')[0] : undefined
                }));
              }} data-id="element-1559" />
                  <label htmlFor="maintenance-toggle" className="text-sm font-medium text-status-warning cursor-pointer" data-id="element-1560">
                    Imposta macchinario in manutenzione
                  </label>
                </div>
                <div className="sm:col-span-2 mt-2" data-id="element-1561">
                  <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1562">
                    Note tecniche
                  </label>
                  <textarea className="medical-input resize-y" rows={3} placeholder="Inserire eventuali note tecniche, interventi previsti o anomalie riscontrate..." value={machineForm.notes || ''} onChange={e => setMachineForm(prev => ({
                ...prev,
                notes: e.target.value
              }))} data-id="element-1563" />
                </div>
              </div>
            </section>}

          {/* Notes only section for add mode */}
          {activeModal === 'add' && <section data-id="element-1564">
              <h3 className="text-section-title text-text-primary mb-4 flex items-center gap-2 pt-6 border-t border-clinical-gray-200" data-id="element-1565">
                <ActivityIcon size={20} className="text-medical-green" data-id="element-1566" /> 5.
                Note
              </h3>
              <p className="text-sm text-text-secondary mb-4" data-id="element-1567">
                Lo stato operativo verrà determinato automaticamente dalla prima
                connessione riuscita tra la piattaforma e il macchinario.
              </p>
              <div data-id="element-1568">
                <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1569">
                  Note tecniche
                </label>
                <textarea className="medical-input resize-y" rows={3} placeholder="Inserire eventuali note tecniche, interventi previsti o anomalie riscontrate..." value={machineForm.notes || ''} onChange={e => setMachineForm(prev => ({
              ...prev,
              notes: e.target.value
            }))} data-id="element-1570" />
              </div>
            </section>}

          {/* Section 6: Log e diagnostica (Read only, only in edit mode) */}
          {activeModal === 'configure' && selectedMachine && <section data-id="element-1571">
              <h3 className="text-section-title text-text-primary mb-4 flex items-center gap-2 pt-6 border-t border-clinical-gray-200" data-id="element-1572">
                <AlertTriangleIcon size={20} className="text-medical-green" data-id="element-1573" />{' '}
                6. Log e diagnostica
              </h3>
              <div className="bg-clinical-gray-50 p-4 rounded-medical border border-clinical-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1574">
                <div data-id="element-1575">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1576">
                    Ultimo test connessione
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1577">
                    {selectedMachine.lastTest}
                  </span>
                </div>
                <div data-id="element-1578">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1579">
                    Ultimo errore rilevato
                  </span>
                  <span className={`text-sm font-medium ${selectedMachine.lastError !== 'Nessuno' ? 'text-status-error' : 'text-text-primary'}`} data-id="element-1580">
                    {selectedMachine.lastError}
                  </span>
                </div>
                <div data-id="element-1581">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1582">
                    Stato sincronizzazione
                  </span>
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${selectedMachine.syncStatus === 'Sincronizzato' ? 'bg-status-success-bg text-status-success' : selectedMachine.syncStatus === 'In attesa' ? 'bg-status-warning-bg text-status-warning' : 'bg-status-error-bg text-status-error'}`} data-id="element-1583">
                    {selectedMachine.syncStatus}
                  </span>
                </div>
              </div>
            </section>}
        </div>
      </ModalDialog>

      {/* Delete Confirmation Modal */}
      <ModalDialog isOpen={activeModal === 'delete'} onClose={() => setActiveModal('detail')} title="Confermare eliminazione macchinario?" maxWidth="max-w-md" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setActiveModal('detail')} data-id="element-1585">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto bg-status-error hover:bg-red-600 focus:ring-status-error/30" onClick={executeDelete} data-id="element-1586">
              <Trash2Icon size={16} className="mr-1.5" data-id="element-1587" /> Elimina macchinario
            </button>
          </>} data-id="element-1584">
        <div className="py-2" data-id="element-1588">
          <p className="text-sm text-text-primary" data-id="element-1589">
            Questa azione rimuoverà il macchinario dalla piattaforma. Vuoi
            procedere?
          </p>
        </div>
      </ModalDialog>

      {/* Toast Notification */}
      <AnimatePresence data-id="element-1590">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className={`fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 sm:bottom-6 z-[9999] flex items-center gap-3 px-4 py-3 rounded-medical-lg shadow-modal border bg-white w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] sm:w-auto sm:max-w-md ${toastMessage.type === 'success' ? 'border-status-success/20' : 'border-status-error/20'}`} data-id="element-1591">
            {toastMessage.type === 'success' ? <CheckCircleIcon className="text-status-success shrink-0" size={20} data-id="element-1592" /> : <XCircleIcon className="text-status-error shrink-0" size={20} data-id="element-1593" />}
            <span className="text-sm font-medium text-text-primary" data-id="element-1594">
              {toastMessage.text}
            </span>
            <button onClick={() => setToastMessage(null)} className="ml-2 p-1 text-text-tertiary hover:text-text-primary rounded-full transition-colors" data-id="element-1595">
              <XIcon size={16} data-id="element-1596" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}